import React from 'react';
import ReactDOM from 'react-dom';
import reportWebVitals from './reportWebVitals';
import * as Sentry from '@sentry/react';

import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/js/bootstrap.bundle';

import 'toastr/build/toastr.min.css';

import './index.scss';

import { App } from './App';
import { getHttpStatus, isClientError } from './utils/get-http-status';
import { QueryClientProvider } from 'react-query';
import { queryClient } from './api/queryClient';
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType
} from 'react-router-dom';

Sentry.init({
	dsn: process.env.REACT_APP_SENTRY_DSN,
	integrations: [
		new Sentry.BrowserTracing({
			tracePropagationTargets: [
				'localhost',
				/^https:\/\/yourserver\.io\/api/
			],
			routingInstrumentation: Sentry.reactRouterV6Instrumentation(
				React.useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes
			)
		})
	],
	tracesSampleRate: 0,
	attachStacktrace: false,
	autoSessionTracking: false,
	sampleRate: 1.0,
	// N-DROP: errors originating from injected blob: scripts are not our
	// code — payment iframes / vendor SDKs load worker bundles as
	// blob:https://pay.curseduca.com/... and re-throw generic runtime
	// errors (e.g. "Cannot read properties of undefined (reading
	// 'addListener')") we can neither reproduce nor fix from this app.
	denyUrls: [/^blob:/],
	beforeSend(event: Sentry.Event, hint: Sentry.EventHint) {
		const error = hint.originalException;

		const message =
			error instanceof Error ? error.message ?? '' : '';

		// BIZ / 4xx policy: a backend 4xx (401/403/404/409/422/429/...)
		// is an expected, handled condition, not a bug to alert on. Drop
		// the Sentry report for any captured client error (e.g. an axios
		// rejection that bubbled up as an unhandled promise rejection).
		// The value still propagates to callers — we only suppress the
		// Sentry event here. 5xx and unknown statuses stay fully visible.
		if (isClientError(getHttpStatus(error, message))) {
			return null;
		}

		// N-DROP: benign noise we never want to see.
		// - tracking hooks re-throw generic errors on best-effort
		//   pixel fetch failures (ad-blocker / no integration)
		// - in-app-browser native bridge injects (not our code)
		// - blocked localStorage in sandboxed iframes
		// NOTE: GTM's fetch failure is now recovered at the source
		// (src/hooks/GTM) with a breadcrumb, so it no longer reaches
		// here as a synthetic error — there is nothing to match on.
		const dropErrors: string[] = [
			'Error while fetch FB Pixel.',
			'Error while fetch Analytics Pixel.',
			'Java object is gone',
			'window.webkit.messageHandlers',
			"Access is denied for this document"
		];
		if (dropErrors.some((msg) => message.includes(msg))) {
			return null;
		}

		// N-DROP: client-side network failures, identified by ORIGIN not
		// by message text. An axios error whose `response` is null/undefined
		// never received an HTTP reply: axios only builds such an error in
		// its xhr adapter's `onerror`/`ontimeout` handlers (createError(...,
		// /* response */ null, ...)). That means the request died at the
		// browser/network layer — DNS failure, CORS block, ad/tracking
		// blocker, offline, connection reset — before any server we own was
		// reached. There is no in-app bug and no failing endpoint to fix
		// (a real 4xx/5xx from our API always carries `error.response`, and
		// is handled by the 4xx policy above / left visible for 5xx).
		//
		// This replaces a prior message-substring collapse ('Network Error'
		// / 'timeout exceeded') that (a) was fragile across browser locales
		// and phrasings and (b) kept ~20% of events, so the issue could
		// never leave the backlog. Matching on the error's own shape is
		// robust regardless of how the browser phrases the message.
		//
		// We only suppress the Sentry *report* — the rejection still
		// propagates to callers (e.g. Checkout's onSubmit catch shows the
		// user a toast), so this is not an in-app mask, just noise removal
		// for an unactionable client-network condition.
		const axiosError = error as
			| { isAxiosError?: boolean; response?: unknown }
			| undefined;
		// `== null` matches both null and undefined — a network-layer
		// axios error carries neither a response nor (in this codebase's
		// axios 0.24) any defined response field.
		if (axiosError?.isAxiosError === true && axiosError.response == null) {
			return null;
		}

		return event;
	}
});

ReactDOM.render(
	<React.StrictMode>
		<QueryClientProvider client={queryClient}>
			<App />
		</QueryClientProvider>
	</React.StrictMode>,
	document.getElementById('root')
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
